import { useId, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  AreaChart,
  Area,
} from "recharts";
import type { LucideIcon } from "lucide-react";
import {
  TrendingUp,
  AlertCircle,
  Package,
  Paintbrush,
  PaintRoller,
  Droplets,
  BrickWall,
  Mountain,
  TreeDeciduous,
  Zap,
  Cable,
  FlaskConical,
  ThermometerSun,
  Shovel,
  CircleDot,
} from "lucide-react";
import { OrderItem } from "@/services/suprimentos";
import { AumentoInsumoStat, computeAumentosPreco } from "../chartUtils";
import { Loader } from "@/components/ui/Loader";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency, formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";
import { lineDots } from "@/utils/rechartsInteraction";

/* ─── Ícone por palavras-chave no nome do insumo ─────────────────────────── */
function iconForInsumo(nome: string): LucideIcon {
  const n = nome.toLowerCase();
  if (/textura|massa corrida|gesso/.test(n)) return PaintRoller;
  if (/tinta|verniz|latex|esmalte|primer/.test(n)) return Paintbrush;
  if (/tijolo|barro|cerâmica|ceramica|bloco|telha/.test(n)) return BrickWall;
  if (/areia|brita|pedra|cascalho/.test(n)) return Mountain;
  if (/cimento|cal\s|argamassa|concreto/.test(n)) return Shovel;
  if (/madeira|compensado|mdf|tábua|tabua|pinus/.test(n)) return TreeDeciduous;
  if (/cabo|fio elétr|fio eletr|eletric/.test(n)) return Cable;
  if (/tubo|cano|pvc|hidrául|hidraul|água fria|agua fria/.test(n)) return Droplets;
  if (/lâmpada|lampada|led|ilumina/.test(n)) return CircleDot;
  if (/isolante|térmic|termic|lã de vidro|la de vidro/.test(n)) return ThermometerSun;
  if (/químico|quimico|solvente|ácido|acido|cola\s/.test(n)) return FlaskConical;
  if (/parafuso|prego|buch|fixador|suporte metal/.test(n)) return Zap;
  return Package;
}

/* ─── Tooltip do gráfico de preço por pedido (modal) ─────────────────────── */
function PriceOrderTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: { orderId: string; qty: number; total: number; date: string };
  }>;
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 min-w-[190px]">
      <p className="text-xs font-semibold text-slate-600 mb-1.5">{d.payload.date}</p>
      <p className="text-xs text-slate-500">
        Pedido:{" "}
        <span className="font-medium text-slate-700 font-mono">#{d.payload.orderId}</span>
      </p>
      <p className="text-sm font-bold text-rose-600 mt-1">{formatCurrency(d.value)}</p>
      <div className="mt-1 flex gap-3 text-xs text-slate-400">
        <span>Qtd.: {formatNumber(d.payload.qty, 2)}</span>
        <span>Total: {formatCurrency(d.payload.total)}</span>
      </div>
    </div>
  );
}

/* ─── Sparkline compacto por card ───────────────────────────────────────── */
function SparklineCard({
  evolucao,
  gradientId,
}: {
  evolucao: AumentoInsumoStat["evolucao"];
  gradientId: string;
}) {
  const data =
    evolucao.length >= 2
      ? evolucao
      : evolucao.length === 1
        ? [...evolucao, { ...evolucao[0], mes: evolucao[0].mes }]
        : [];

  if (data.length < 2) {
    return <div className="h-14 rounded-md bg-rose-50/50 border border-rose-100/80" />;
  }

  return (
    <ResponsiveContainer width="100%" height={56}>
      <AreaChart data={data} margin={{ top: 6, right: 4, left: 4, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fb7185" stopOpacity={0.45} />
            <stop offset="100%" stopColor="#fb7185" stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="precoMedio"
          stroke="#f43f5e"
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={{ r: 2.5, fill: "#f43f5e", stroke: "#fff", strokeWidth: 1 }}
          activeDot={{ r: 4 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/* ─── Abas modal ───────────────────────────────────────────────────────── */
type Tab = "evolucao" | "pedidos";

interface Props {
  orderItems: OrderItem[];
  loading:    boolean;
  error?:     string | null;
}

export function AumentosPreco({ orderItems, loading, error }: Props) {
  const [selected, setSelected] = useState<AumentoInsumoStat | null>(null);
  const [tab, setTab]           = useState<Tab>("evolucao");
  const baseId                  = useId();

  const data = useMemo(() => computeAumentosPreco(orderItems), [orderItems]);

  const modalPedidos = useMemo(() => {
    if (!selected) return [];
    const grouped = new Map<string, { items: OrderItem[]; date?: string }>();
    for (const it of selected.rawItems) {
      if (!grouped.has(it.orderId)) grouped.set(it.orderId, { items: [], date: it.orderDate });
      grouped.get(it.orderId)!.items.push(it);
    }
    return Array.from(grouped.entries())
      .map(([orderId, { items, date }]) => ({
        orderId,
        date: date?.substring(0, 10) ?? "—",
        qty:  items.reduce((s, i) => s + (i.quantity ?? 0), 0),
        price: items[0].netPrice > 0 ? items[0].netPrice : items[0].unitPrice,
        total: items.reduce((s, i) => s + (i.totalAmount ?? 0), 0),
      }))
      .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
  }, [selected]);

  const openModal = (entry: AumentoInsumoStat) => {
    setSelected(entry);
    setTab("evolucao");
  };

  /* ─── Estados especiais ──────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-rose-500" />
          <h3 className="text-sm font-semibold text-slate-700">Maiores Aumentos de Preço por Insumo</h3>
        </div>
        <div className="h-56 flex flex-col items-center justify-center gap-3 text-slate-400">
          <Loader size="md" label="Calculando variações de preço…" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-red-500" />
          <h3 className="text-sm font-semibold text-slate-700">Maiores Aumentos de Preço por Insumo</h3>
        </div>
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-rose-500" />
          <h3 className="text-sm font-semibold text-slate-700">Maiores Aumentos de Preço por Insumo</h3>
        </div>
        <div className="h-40 flex items-center justify-center text-sm text-slate-400">
          Nenhum insumo com variação de preço detectável no período selecionado.
          <br />
          <span className="text-xs text-slate-300 mt-1 block">
            São necessários dados de pelo menos 2 meses para calcular a variação.
          </span>
        </div>
      </div>
    );
  }

  const leader = data[0];

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-5 flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-rose-500 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-slate-800">
                Maiores Aumentos de Preço por Insumo
              </h3>
            </div>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">
              Variação do preço médio entre o 1º e o último mês com compras. Clique para ver a evolução.
            </p>
          </div>
          <span className="text-xs text-slate-500 bg-slate-50 px-3 py-1 rounded-full border border-slate-100 self-start">
            {data.length} insumo{data.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((item, index) => {
            const rank = index + 1;
            const Icon = iconForInsumo(item.nome);
            const gradId = `${baseId}-spark-${rank}`;
            const pctLabel = `+${formatNumber(item.pctAumento, 1)}%`;

            return (
              <button
                key={`${item.nome}-${rank}`}
                type="button"
                onClick={() => openModal(item)}
                className={cn(
                  "text-left rounded-xl border border-slate-200 bg-white p-4 shadow-sm",
                  "transition-all duration-200 ease-out",
                  "hover:border-rose-200 hover:shadow-md hover:-translate-y-0.5",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2"
                )}
              >
                <div className="flex items-start gap-2 mb-3">
                  <span
                    className="flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-rose-100 text-[11px] font-bold text-rose-600 tabular-nums"
                    aria-hidden
                  >
                    {rank}
                  </span>
                  <p className="text-[13px] font-semibold text-slate-800 leading-snug line-clamp-2 min-h-[2.5rem]">
                    {item.nomeAbrev}
                  </p>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-500">
                    <Icon className="h-6 w-6 stroke-[1.5]" aria-hidden />
                  </div>
                  <p className="flex-1 text-right text-2xl font-bold tracking-tight text-rose-600 tabular-nums">
                    {pctLabel}
                  </p>
                </div>

                <div className="mb-2 overflow-hidden rounded-lg border border-rose-100/80 bg-rose-50/30">
                  <SparklineCard evolucao={item.evolucao} gradientId={gradId} />
                </div>

                <div className="flex justify-center">
                  <span className="inline-flex rounded-full bg-rose-50 px-3 py-1 text-[11px] font-semibold text-rose-600 tabular-nums border border-rose-100">
                    {pctLabel}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="rounded-lg bg-slate-100 px-4 py-3 text-center text-xs sm:text-sm text-slate-600 leading-relaxed">
          O insumo{" "}
          <span className="font-semibold text-slate-800">{leader.nome}</span> teve o maior aumento de
          preço:{" "}
          <span className="font-bold text-rose-600 tabular-nums">
            +{formatNumber(leader.pctAumento, 1)}%
          </span>{" "}
          no período analisado.
        </div>
      </div>

      {selected && (
        <Modal
          open
          onClose={() => setSelected(null)}
          title={
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-rose-50 flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-rose-500" />
              </div>
              <span className="text-base font-bold text-slate-800 uppercase tracking-wide leading-tight truncate">
                {selected.nome}
              </span>
            </div>
          }
          size="lg"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-50 rounded-xl px-4 py-3 text-center border border-slate-100">
                <p className="text-xs text-slate-500 mb-1">Preço Inicial</p>
                <p className="text-base font-bold text-slate-700">{formatCurrency(selected.precoInicial)}</p>
                <p className="text-[10px] text-slate-400">{selected.evolucao[0]?.mes ?? "—"}</p>
              </div>
              <div className="bg-rose-50 rounded-xl px-4 py-3 text-center border border-rose-100">
                <p className="text-xs text-slate-500 mb-1">Aumento</p>
                <p className="text-base font-bold text-rose-600">+{formatNumber(selected.pctAumento, 1)}%</p>
                <p className="text-[10px] text-slate-400">
                  {selected.orderIds.length} pedido{selected.orderIds.length !== 1 ? "s" : ""}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl px-4 py-3 text-center border border-slate-100">
                <p className="text-xs text-slate-500 mb-1">Preço Final</p>
                <p className="text-base font-bold text-slate-700">{formatCurrency(selected.precoFinal)}</p>
                <p className="text-[10px] text-slate-400">
                  {selected.evolucao[selected.evolucao.length - 1]?.mes ?? "—"}
                </p>
              </div>
            </div>

            <div className="flex gap-1 border-b border-slate-200">
              {(["evolucao", "pedidos"] as Tab[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-t-md border-b-2 transition-colors",
                    tab === t
                      ? "border-rose-500 text-rose-700 bg-rose-50"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  )}
                >
                  {t === "evolucao" ? "Evolução de Preço" : `Pedidos (${selected.orderIds.length})`}
                </button>
              ))}
            </div>

            {tab === "evolucao" && (
              <div>
                {modalPedidos.length < 2 ? (
                  <p className="text-sm text-slate-400 text-center py-8">
                    Dados insuficientes para plotar evolução.
                  </p>
                ) : (
                  <>
                    <p className="text-[11px] text-slate-400 mb-2">
                      Preço unitário (R$) de cada pedido ao longo do tempo. Passe o mouse sobre os pontos para ver detalhes.
                    </p>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart
                        data={modalPedidos}
                        margin={{ left: 8, right: 24, top: 8, bottom: 32 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 9, fill: "#94a3b8" }}
                          axisLine={false}
                          tickLine={false}
                          angle={-35}
                          textAnchor="end"
                          height={52}
                          interval={modalPedidos.length > 12 ? Math.floor(modalPedidos.length / 8) : 0}
                        />
                        <YAxis
                          tickFormatter={(v: number) => formatCurrency(v)}
                          tick={{ fontSize: 10, fill: "#94a3b8" }}
                          axisLine={false}
                          tickLine={false}
                          width={82}
                        />
                        <Tooltip content={<PriceOrderTooltip />} />
                        <ReferenceLine
                          y={selected.precoInicial}
                          stroke="#94a3b8"
                          strokeDasharray="4 2"
                          label={{
                            value: `Inicial: ${formatCurrency(selected.precoInicial)}`,
                            position: "insideTopLeft",
                            fontSize: 9,
                            fill: "#94a3b8",
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="price"
                          name="Preço Unit."
                          stroke="#f43f5e"
                          strokeWidth={2.5}
                          {...lineDots("#f43f5e", { dotR: 4, activeR: 7 })}
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                )}
              </div>
            )}

            {tab === "pedidos" && (
              <div className="overflow-x-auto max-h-72 overflow-y-auto rounded-xl border border-slate-200">
                {modalPedidos.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">Nenhum pedido disponível.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Pedido</th>
                        <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Data</th>
                        <th className="text-center px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Qtd.</th>
                        <th className="text-right px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Preço Unit.</th>
                        <th className="text-right px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {modalPedidos.map((row) => (
                        <tr key={row.orderId} className="hover:bg-slate-50 transition-colors">
                          <td className="px-3 py-2 font-mono text-slate-700">#{row.orderId}</td>
                          <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{row.date}</td>
                          <td className="px-3 py-2 text-center text-slate-600">{formatNumber(row.qty, 2)}</td>
                          <td className="px-3 py-2 text-right text-slate-700">{formatCurrency(row.price)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-800">{formatCurrency(row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                      <tr>
                        <td colSpan={4} className="px-3 py-2 text-xs font-semibold text-slate-400">
                          {modalPedidos.length} pedido{modalPedidos.length !== 1 ? "s" : ""}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-slate-800">
                          {formatCurrency(modalPedidos.reduce((s, r) => s + r.total, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
